import React, { useEffect, useMemo, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { closeGameRules, closeMenu } from "../../../../State/actions";
import { selectGameRulesActive } from "../../../../State/selectors";
import { selectRulesHtml } from "../../../../State/Slices/assetsSlice";
import { PixiSettings } from "../../../../pixiSettings";
import { useSecondaryToolbar } from "../../../../Hooks/useSecondaryToolbar";
import { useIosOverlayGuard } from "../../../../Hooks/useIosOverlayGuard";
import { useOverlayFade } from "../../../../Hooks/useOverlayFade";
import { getUITextureAsDataURL, getGameImageSrc } from "../../../../assets";
import { soundManager } from "../../../../SoundManager";
import { SOUND_NAME } from "../../../../sounds";
import {
  BASE_FONT_SIZE,
  CHROME_DEVICE_CONFIG,
  CHROME_RATIOS,
  getDeviceConfig,
  isBrowserChromeShortenedPortrait,
} from "./paytableConfig";



const GameRulesOverlay = () => {
  const dispatch = useDispatch();
  const gameRulesActive = useSelector(selectGameRulesActive);
  const { isHidden } = useIosOverlayGuard();
  const { t } = useTranslation();
  const { dimensions, device, orientation, uiSymbolScale, isMobileSmall } = useDimensions();
  const toolbar = useSecondaryToolbar();

  // Mount + fade lifecycle — shared hook (animejs on the RAF ticker, no timers).
  const { shouldRender, overlayRef } = useOverlayFade({
    active: gameRulesActive,
    isHidden,
  });

  // Use pre-cached rules HTML from Redux (loaded during asset phase)
  const htmlContent = useSelector(selectRulesHtml);
  const scrollRef = useRef(null);
  const scrollThumbRef = useRef(null);

  // ── Image cache — extract data URLs once when overlay mounts ──
  // Same pattern as PaytableOverlay.gameImages: useMemo runs once,
  // all render calls read from the map — zero canvas work during re-renders.
  const cachedImages = useMemo(() => {
    if (!shouldRender) return { closeIcon: "", gameLogo: "", companyLogo: "" };
    return {
      closeIcon: getUITextureAsDataURL("button_x"),
      gameLogo: getGameImageSrc("logo") || "",
      companyLogo: getUITextureAsDataURL("company_logo"),
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRender]);

  // ── Chrome sizing — shared with PaytableOverlay for consistent spacing ──
  const isPortrait =
    (device === PixiSettings.DEVICE.MOBILE || device === PixiSettings.DEVICE.TABLET) &&
    orientation === PixiSettings.ORIENTATION.PORTRAIT;

  const chrome = useMemo(() => {
    const config = getDeviceConfig(CHROME_DEVICE_CONFIG, device, orientation);
    const base = BASE_FONT_SIZE * config.fontMultiplier * uiSymbolScale;
    return {
      title:     Math.round(base * CHROME_RATIOS.title),
      close:     Math.round(base * CHROME_RATIOS.close),
      separator: Math.round(base * CHROME_RATIOS.separatorHeight),
      topPad:    Math.round(base * CHROME_RATIOS.topPad),
      base,
    };
  }, [device, orientation, uiSymbolScale]);

  // Footer padding — standard mobile gets a much larger bottom zone (5× previous multipliers)
  const isStandardMobile =
    device === PixiSettings.DEVICE.MOBILE && !isMobileSmall;
  // Same gate as PaytableOverlay — fullscreen tall phones stay standard layout
  const isShortStandardPortrait =
    isStandardMobile &&
    isPortrait &&
    isBrowserChromeShortenedPortrait(dimensions.height);
  const layoutIsStandardMobile = isStandardMobile && !isShortStandardPortrait;
  const layoutIsMobileSmall = isMobileSmall || isShortStandardPortrait;
  const isTablet = device === PixiSettings.DEVICE.TABLET;
  const bottomPad = layoutIsStandardMobile
    ? isPortrait
      ? chrome.topPad * 7.5
      : chrome.topPad * 4.5
    : layoutIsMobileSmall
      ? isPortrait
        ? chrome.topPad * 6.5
        : chrome.topPad * 4.5
      : isTablet
        ? isPortrait
          ? chrome.topPad * 7.5
          : chrome.topPad * 5.0
        : device === PixiSettings.DEVICE.DESKTOP
          ? chrome.topPad * 3.75
          : isPortrait
            ? chrome.topPad * 2.0
            : chrome.topPad * 2.5;
  const topPad = layoutIsStandardMobile || layoutIsMobileSmall || isTablet
    ? isPortrait
      ? chrome.topPad * 3.0
      : chrome.topPad * 1.5
    : chrome.topPad;
  const linePad = Math.round(chrome.title * 0.5);

  // Calculate viewport configuration (same logic as PixiJS GameRules)
  const viewportConfig = useMemo(() => {
    const getViewportConfig = () => {
      if (device === PixiSettings.DEVICE.MOBILE) {
        if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
          return {
            maxWidth: dimensions.width * (layoutIsMobileSmall ? 0.8 : 0.75),
            paddingTop: dimensions.height / 5.5,
            paddingBottom: dimensions.height / 10,
            titleFontSizeMultiplier: 1.35,
            closeButtonOffsetMultiplier: 1.65,
            logoSizeMultiplier: 1.5,
          };
        } else {
          return {
            maxWidth: toolbar.width * (layoutIsMobileSmall ? 0.575 : 0.55),
            paddingTop: dimensions.height / 3.0,
            paddingBottom: dimensions.height / 8.5,
            titleFontSizeMultiplier: 1.1,
            closeButtonOffsetMultiplier: 1.35,
            logoSizeMultiplier: 0.8,
          };
        }
      } else if (device === PixiSettings.DEVICE.TABLET) {
        if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
          return {
            maxWidth: dimensions.width * 0.75,
            paddingTop: dimensions.height / 4.6,
            paddingBottom: dimensions.height / 9.5,
            titleFontSizeMultiplier: 1.45,
            closeButtonOffsetMultiplier: 1.75,
            logoSizeMultiplier: 1.75,
          };
        } else {
          return {
            maxWidth: toolbar.width * 0.65,
            paddingTop: dimensions.height / 4.5,
            paddingBottom: dimensions.height / 9.5,
            titleFontSizeMultiplier: 1.0,
            closeButtonOffsetMultiplier: 1.65,
            logoSizeMultiplier: 1.5,
          };
        }
      } else {
        return {
          maxWidth: toolbar.width * 0.51,
          paddingTop: dimensions.height / 4,
          paddingBottom: dimensions.height / 8,
          titleFontSizeMultiplier: 1.0,
          closeButtonOffsetMultiplier: 1.85,
          logoSizeMultiplier: 1.0,
        };
      }
    };

    const config = getViewportConfig();
    const viewportHeight =
      dimensions.height - config.paddingTop - config.paddingBottom;

    return {
      ...config,
      viewportHeight,
    };
  }, [dimensions, device, orientation, toolbar.width, layoutIsMobileSmall]);

  // Calculate font sizes (matching PixiJS logic)
  const baseTitleFontSize = 56;
  const baseParagraphFontSize = 28;
  const baseSectionTitleFontSize = 32;

  const fontSizes = useMemo(() => {
    let titleMultiplier = 1.1;
    let paragraphMultiplier = 1.1;

    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        titleMultiplier = 1.9;
        paragraphMultiplier = 1.9;
      } else {
        titleMultiplier = 1.15;
        paragraphMultiplier = 1.15;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        titleMultiplier = 2.2;
        paragraphMultiplier = 2.2;
      } else {
        titleMultiplier = 1.6;
        paragraphMultiplier = 1.5;
      }
    }

    return {
      title: Math.floor(
        baseTitleFontSize * viewportConfig.titleFontSizeMultiplier * uiSymbolScale
      ),
      sectionTitle: Math.floor(
        baseSectionTitleFontSize * titleMultiplier * uiSymbolScale
      ),
      paragraph: Math.floor(
        baseParagraphFontSize * paragraphMultiplier * uiSymbolScale
      ),
    };
  }, [
    device,
    orientation,
    uiSymbolScale,
    viewportConfig.titleFontSizeMultiplier,
  ]);

  const headerTitleFontSize = (layoutIsStandardMobile || isTablet) && isPortrait
    ? Math.round(fontSizes.title * 1.06)
    : (layoutIsStandardMobile || layoutIsMobileSmall || isTablet) && !isPortrait
      ? Math.round(fontSizes.title * 0.9)
      : layoutIsMobileSmall && isPortrait
        ? Math.round(fontSizes.title * 0.94)
        : device === PixiSettings.DEVICE.DESKTOP
          ? Math.round(fontSizes.title * 0.9)
          : fontSizes.title;

  const headerBottomPad = (layoutIsStandardMobile || layoutIsMobileSmall) && !isPortrait
    ? Math.round(linePad * 0.5)
    : isTablet && !isPortrait
      ? Math.round(linePad * 1.0)
      : device === PixiSettings.DEVICE.DESKTOP
        ? Math.round(linePad * 0.15)
        : Math.round(linePad * 1.5);

  const contentFontSizes = useMemo(() => {
    const scale = (layoutIsStandardMobile || isTablet) && isPortrait
      ? 1.06
      : (layoutIsStandardMobile || layoutIsMobileSmall) && !isPortrait
        ? 0.94
        : device === PixiSettings.DEVICE.DESKTOP
          ? 0.94
          : 1;
    return {
      sectionTitle: Math.round(fontSizes.sectionTitle * scale),
      paragraph: Math.round(fontSizes.paragraph * scale),
    };
  }, [fontSizes.sectionTitle, fontSizes.paragraph, layoutIsStandardMobile, layoutIsMobileSmall, isTablet, isPortrait, device]);

  const isTunedMobile = layoutIsStandardMobile || layoutIsMobileSmall;
  const isTunedMobileOrTablet = isTunedMobile || isTablet;
  const isDesktop = device === PixiSettings.DEVICE.DESKTOP;
  const h3MarginMul = isTunedMobileOrTablet
    ? isPortrait
      ? 1.2
      : 1.75
    : null;
  const h3MarginTopMul = isDesktop ? 1.75 : h3MarginMul ?? 1.2;
  const h3MarginBottomMul = isDesktop ? 1.75 : h3MarginMul ?? 0.9;

  const companyLogoHeight = useMemo(() => {
    if ((layoutIsStandardMobile || isTablet) && isPortrait) {
      return Math.round(bottomPad * 0.44);
    }

    if (layoutIsMobileSmall && isPortrait) {
      return Math.round(bottomPad * 0.44);
    }

    if ((layoutIsStandardMobile || layoutIsMobileSmall || isTablet) && !isPortrait) {
      const baseHeight = Math.round(
        Math.min(fontSizes.paragraph * 1.2, 34 * uiSymbolScale),
      );
      return Math.round(baseHeight * 1.2);
    }

    if (device === PixiSettings.DEVICE.DESKTOP) {
      return Math.round(bottomPad * 0.44);
    }

    return Math.round(Math.min(fontSizes.paragraph * 1.2, 34 * uiSymbolScale));
  }, [
    device,
    layoutIsStandardMobile,
    layoutIsMobileSmall,
    isTablet,
    isPortrait,
    bottomPad,
    fontSizes.paragraph,
    uiSymbolScale,
  ]);

  // Custom absolute scrollbar — universal (same look + offset as standard mobile)
  const scrollbarGutter = isPortrait
    ? layoutIsMobileSmall
      ? 12
      : 24
    : 12;
  const scrollbarRailInset = 8;
  const scrollbarWidth = device === PixiSettings.DEVICE.DESKTOP
    ? 10
    : isPortrait
      ? 8
      : 5;
  const scrollbarTrackColor = "#383838";
  const scrollbarThumbColor = "#a0a0a0";

  const updateCustomScrollbar = useCallback(() => {
    const el = scrollRef.current;
    const thumb = scrollThumbRef.current;
    if (!el || !thumb || !scrollbarGutter) return;

    const { scrollHeight, clientHeight, scrollTop } = el;
    const trackHeight = clientHeight - scrollbarRailInset * 2;
    if (scrollHeight <= clientHeight || trackHeight <= 0) {
      thumb.style.display = "none";
      return;
    }

    thumb.style.display = "block";
    const thumbHeight = Math.max(32, (clientHeight / scrollHeight) * trackHeight);
    const maxThumbTop = trackHeight - thumbHeight;
    const thumbTop =
      maxThumbTop <= 0 ? 0 : (scrollTop / (scrollHeight - clientHeight)) * maxThumbTop;
    thumb.style.height = `${Math.round(thumbHeight)}px`;
    thumb.style.transform = `translateY(${Math.round(thumbTop)}px)`;
  }, [scrollbarGutter, scrollbarRailInset]);

  // Handle close
  const handleClose = useCallback(() => {
    // Play click sound
    soundManager.play(SOUND_NAME.button_click);
    
    dispatch(closeGameRules());
    dispatch(closeMenu());
  }, [dispatch]);

  // Calculate logo size based on device configuration
  const logoSize = useMemo(() => {
    const baseWidth = 300;
    const baseHeight = 150;
    const headerScale = layoutIsMobileSmall && isPortrait
      ? 0.94
      : device === PixiSettings.DEVICE.DESKTOP
        ? 0.90
        : 1;

    return {
      width: baseWidth * uiSymbolScale * viewportConfig.logoSizeMultiplier * headerScale,
      height: baseHeight * uiSymbolScale * viewportConfig.logoSizeMultiplier * headerScale,
    };
  }, [uiSymbolScale, viewportConfig.logoSizeMultiplier, layoutIsMobileSmall, isPortrait, device]);

  const closeButtonSize = device === PixiSettings.DEVICE.DESKTOP
    ? Math.round(chrome.close * 0.65)
    : chrome.close;

  // ── Separator width — matches paytable pattern ──
  const separatorLineWidth = (layoutIsStandardMobile || layoutIsMobileSmall || isTablet) && !isPortrait
    ? Math.round(viewportConfig.maxWidth * 1.1)
    : device === PixiSettings.DEVICE.DESKTOP
      ? Math.round(Math.min(chrome.title * 16, dimensions.width * 0.62) * 1.1)
      : isPortrait
        ? Math.min(chrome.title * 12, dimensions.width * 0.95)
        : Math.min(chrome.title * 16, dimensions.width * 0.62);

  const separatorHeightMul = device === PixiSettings.DEVICE.DESKTOP ? 0.6 : 1.25;
  const separatorTopMarginMul = device === PixiSettings.DEVICE.DESKTOP ? 0.08 : 0.3;

  // Horizontal inset — matches content column side padding (close button right offset)
  const closeButtonRightOffset = Math.max(
    4,
    Math.round((dimensions.width - viewportConfig.maxWidth) / 2) -
      Math.round(chrome.close * 0.8) +
      (device === PixiSettings.DEVICE.DESKTOP ? Math.round(chrome.close * 0.5) : 0),
  );

  // Custom absolute scrollbar — sync thumb via refs (no scroll re-renders)
  useEffect(() => {
    if (!shouldRender || !scrollbarGutter) return;

    const el = scrollRef.current;
    if (!el) return;

    updateCustomScrollbar();
    el.addEventListener("scroll", updateCustomScrollbar, { passive: true });
    const resizeObserver = new ResizeObserver(updateCustomScrollbar);
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener("scroll", updateCustomScrollbar);
      resizeObserver.disconnect();
    };
  }, [shouldRender, scrollbarGutter, htmlContent, updateCustomScrollbar]);


  if (!shouldRender) {
    return null;
  }

  return (
    <div
      ref={overlayRef}
      className="game-rules-overlay"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        // Visible-area size (excludes browser toolbars). 100vw/100vh on iOS
        // Safari resolve to the taller layout viewport, which pushes the footer
        // behind the bottom toolbar. dimensions.* come from visualViewport (the
        // real visible area) — matches PaytableOverlay.
        width: dimensions.width,
        height: dimensions.height,
        background: "radial-gradient(ellipse at center, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.92) 75%, rgba(0,0,0,0.97) 100%)",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: `${topPad}px 0 0 0`,
        boxSizing: "border-box",
        pointerEvents: isHidden ? "none" : "auto",
        // Starts hidden; animejs drives opacity from useOverlayFade.
        opacity: 0,
      }}
    >
      {/* ── HEADER: logo + separator + title (flex-shrink: 0, matches paytable .spt-header) ── */}
      <div
        style={{
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          paddingBottom: `${headerBottomPad}px`,
          position: "relative",
          width: "100%",
          zIndex: 1,
        }}
      >
        {/* Logo row — close button vertically centered on logo only */}
        <div
          style={{
            position: "relative",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <button
            className="gr-close-btn"
            onClick={handleClose}
            style={{
              position: "absolute",
              top: "50%",
              right: `${closeButtonRightOffset}px`,
              transform: "translateY(-50%)",
              width: `${closeButtonSize}px`,
              height: `${closeButtonSize}px`,
              backgroundColor: "transparent",
              border: "none",
              cursor: "pointer",
              padding: 0,
              zIndex: 1010,
              backgroundImage: cachedImages.closeIcon
                ? `url('${cachedImages.closeIcon}')`
                : "none",
              backgroundSize: "contain",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              transition: "filter 0.2s ease",
            }}
          />
          <img
            src={cachedImages.gameLogo}
            alt="Game Logo"
            style={{
              maxWidth: `${logoSize.width}px`,
              maxHeight: `${logoSize.height}px`,
              objectFit: "contain",
            }}
          />
        </div>

        {/* Separator — matches paytable .spt-separator */}
        <div
          style={{
            width: `${separatorLineWidth}px`,
            height: `${Math.round(chrome.separator * separatorHeightMul)}px`,
            background: "linear-gradient(to right, transparent, #ffc102 35%, #ffc102 65%, transparent)",
            clipPath: "ellipse(50% 50% at 50% 50%)",
            marginTop: `${Math.round(linePad * separatorTopMarginMul)}px`,
          }}
        />

        {/* Title */}
        <h1
          style={{
            fontFamily: "Noto Sans, sans-serif",
            fontSize: `${headerTitleFontSize}px`,
            fontWeight: 800,
            color: "#ffc102",
            textShadow: "0 2px 6px rgba(0, 0, 0, 0.5)",
            margin: `${Math.round(linePad * 0.3)}px 0 0 0`,
            textAlign: "center",
            whiteSpace: "nowrap",
          }}
        >
          {t("rules_title", { ns: "common" })}
        </h1>
      </div>

      {/* ── CONTENT: scrollable HTML (flex: 1, matches paytable slide area) ── */}
      <div
        className="game-rules-scroll-wrap"
        style={{
          flex: 1,
          minHeight: 0,
          width: `${viewportConfig.maxWidth}px`,
          position: "relative",
        }}
      >
        <div
          ref={scrollRef}
          className={`game-rules-content${scrollbarGutter ? " gr-hide-native-scrollbar" : ""}`}
          style={{
            // Absolute inset (not height:100%) so the scroll viewport has a
            // concrete height against the position:relative wrap. A percentage
            // height against a flex:1 parent collapses on iOS Safari, killing
            // the scroll — the content then only paints one screenful.
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflowY: "auto",
            overflowX: "hidden",
            WebkitOverflowScrolling: "touch",
            fontFamily: "Noto Sans, sans-serif",
            color: "#ffffff",
            padding: "0",
            scrollbarWidth: scrollbarGutter ? "none" : "thin",
            scrollbarColor: "#ffc102 rgba(255, 255, 255, 0.1)",
          }}
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
        {scrollbarGutter > 0 && (
          <div
            aria-hidden="true"
            className="gr-scrollbar-rail"
            style={{
              position: "absolute",
              top: `${scrollbarRailInset}px`,
              right: `-${scrollbarGutter}px`,
              bottom: `${scrollbarRailInset}px`,
              width: `${scrollbarWidth}px`,
              borderRadius: `${scrollbarWidth / 2}px`,
              background: scrollbarTrackColor,
              pointerEvents: "none",
            }}
          >
            <div
              ref={scrollThumbRef}
              style={{
                width: "100%",
                background: scrollbarThumbColor,
                borderRadius: `${scrollbarWidth / 2}px`,
                display: "none",
                willChange: "transform",
              }}
            />
          </div>
        )}
      </div>

      {/* Inject dynamic styles for content */}
      <style>{`
        .game-rules-overlay {
          font-family: 'Noto Sans', sans-serif;
        }

        .game-rules-content h3 {
          font-size: ${contentFontSizes.sectionTitle}px;
          font-weight: 700;
          color: #ffc102;
          text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
          margin: ${contentFontSizes.sectionTitle * h3MarginTopMul}px 0 ${
        contentFontSizes.sectionTitle * h3MarginBottomMul
      }px 0;
          line-height: ${contentFontSizes.sectionTitle * 1.2}px;
        }

        .game-rules-content section:first-of-type h3:first-of-type,
        .game-rules-content > h3:first-of-type {
          margin-top: 0;
        }

        .game-rules-content p {
          font-size: ${contentFontSizes.paragraph}px;
          font-weight: 400;
          color: #ffffff;
          margin: 0 0 ${contentFontSizes.paragraph * 0.9}px 0;
          line-height: ${contentFontSizes.paragraph * 1.3}px;
        }

        .game-rules-content ul {
          margin: 0 0 ${contentFontSizes.paragraph * 0.9}px 0;
          padding-left: ${contentFontSizes.paragraph * 1.5}px;
        }

        .game-rules-content ol {
          margin: 0 0 ${contentFontSizes.paragraph * 0.9}px 0;
          padding-left: ${contentFontSizes.paragraph * 1.5}px;
          list-style-type: decimal;
        }

        .game-rules-content li {
          font-size: ${contentFontSizes.paragraph}px;
          font-weight: 400;
          color: #ffffff;
          margin-bottom: ${contentFontSizes.paragraph * 0.35}px;
          line-height: ${contentFontSizes.paragraph * 1.3}px;
        }

        .game-rules-content section {
          margin-bottom: ${contentFontSizes.paragraph * 0.65}px;
        }

        .game-rules-content strong {
          font-weight: 700;
        }

        .gr-hide-native-scrollbar {
          scrollbar-width: none;
        }

        .gr-hide-native-scrollbar::-webkit-scrollbar {
          display: none;
          width: 0;
          height: 0;
        }

        /* Custom scrollbar for webkit browsers (default path) */
        .game-rules-content:not(.gr-hide-native-scrollbar)::-webkit-scrollbar {
          width: 8px;
        }

        .game-rules-content:not(.gr-hide-native-scrollbar)::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 4px;
        }

        .game-rules-content:not(.gr-hide-native-scrollbar)::-webkit-scrollbar-thumb {
          background: #ffc102;
          border-radius: 4px;
        }

        .game-rules-content:not(.gr-hide-native-scrollbar)::-webkit-scrollbar-thumb:hover {
          background: #ffd644;
        }

        .gr-close-btn:hover { filter: brightness(2.0); }
      `}</style>

      {/* Company logo footer — flex-centered in bottomPad zone */}
      <div
        style={{
          flexShrink: 0,
          height: `${bottomPad}px`,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {cachedImages.companyLogo && (
          <img
            src={cachedImages.companyLogo}
            alt=""
            aria-hidden="true"
            style={{
              height: `${companyLogoHeight}px`,
              objectFit: "contain",
              opacity: 0.9,
            }}
          />
        )}
      </div>
    </div>
  );
};

export default GameRulesOverlay;

